import type { PlayerShip, Escalation, SpatialState } from "../types/game";

interface Props {
  ship: PlayerShip;
  escalation: Escalation;
  spatialState?: SpatialState | null;
}

const SPEED_LABELS: Record<string, string> = {
  all_stop: "ALL STOP",
  impulse_slow: "1/4 IMPULSE",
  impulse_half: "1/2 IMPULSE",
  impulse_full: "FULL IMPULSE",
  orbit: "STANDARD ORBIT",
  warp: "WARP",
};

const CONDITION_COLORS: Record<string, string> = {
  healthy: "var(--color-green)",
  damaged: "var(--color-yellow)",
  badly_damaged: "var(--color-orange)",
  critical: "var(--color-red)",
  destroyed: "var(--color-red)",
};

// The DM sometimes returns objects instead of strings for fields like shields.
// Normalize to a display string.
function str(val: unknown): string {
  if (typeof val === "string") return val;
  if (val && typeof val === "object" && "status" in val) return String((val as any).status);
  return String(val ?? "unknown");
}

export function ShipStatus({ ship, escalation, spatialState }: Props) {
  const speed = str(spatialState?.playerShip.speed ?? "all_stop");
  const liveShields = str(spatialState?.playerShip.shields ?? ship.shields);
  const heading = spatialState?.playerShip.heading;
  const headingDeg = heading
    ? Math.round(Math.atan2(heading.x, heading.z) * (180 / Math.PI) + 360) % 360
    : 0;
  const pitchDeg = heading
    ? Math.round(Math.asin(Math.max(-1, Math.min(1, heading.y / Math.sqrt(heading.x ** 2 + heading.y ** 2 + heading.z ** 2 || 1)))) * (180 / Math.PI))
    : 0;

  return (
    <div className="ship-status">
      <div className="panel-header">SHIP STATUS</div>
      <div className="status-section">
        <div className="status-row">
          <span className="status-label">SPEED</span>
          <span className={`status-value ${speed === "all_stop" ? "status-down" : "status-up"}`}>
            {SPEED_LABELS[speed] || speed.toUpperCase()}
          </span>
        </div>
        <div className="status-row">
          <span className="status-label">HEADING</span>
          <span className="status-value">
            {String(headingDeg).padStart(3, "0")} MK {pitchDeg >= 0 ? "+" : ""}{pitchDeg}
          </span>
        </div>
      </div>

      <div className="status-divider" />

      <div className="status-section">
        <div className="status-row">
          <span className="status-label">HULL</span>
          <span
            className="status-value"
            style={{ color: CONDITION_COLORS[str(ship.condition)] }}
          >
            {str(ship.condition).replace("_", " ").toUpperCase()}
          </span>
        </div>
        <div className="status-row">
          <span className="status-label">SHIELDS</span>
          <span
            className={`status-value ${liveShields === "up" ? "status-up" : "status-down"}`}
          >
            {liveShields === "up"
              ? `UP ${ship.shieldStrength ?? 100}%`
              : "DOWN"}
          </span>
        </div>
        <div className="status-row">
          <span className="status-label">WEAPONS</span>
          <span className={`status-value status-${str(ship.weapons)}`}>
            {str(ship.weapons).toUpperCase()}
          </span>
        </div>
      </div>

      <div className="status-divider" />

      <div className="status-section">
        <div className="status-row">
          <span className="status-label">ESCALATION</span>
          <span className={`status-value escalation-${str(escalation)}`}>
            {str(escalation).toUpperCase()}
          </span>
        </div>
      </div>

      <div className="status-divider" />

      <div className="status-section">
        <div className="panel-subheader">SYSTEMS</div>
        {Object.entries(ship.systems).map(([system, val]) => {
          const status = typeof val === "object" && val && "status" in val ? (val as any).status : String(val);
          const efficiency = typeof val === "object" && val && "efficiency" in val ? (val as any).efficiency : null;
          const isOk = status === "operational" && (efficiency === null || efficiency >= 80);
          return (
            <div key={system} className="status-row system-row">
              <span className="status-label">{system.toUpperCase()}</span>
              <span className={`status-value ${isOk ? "status-ok" : "status-warn"}`}>
                {isOk ? "OK" : efficiency != null ? `${status.toUpperCase()} ${efficiency}%` : String(status).toUpperCase()}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
